import { Button, CustomTag, Typography } from "@aph/ui-lib-humanx";
import { useApolloClient } from "@apollo/client";
import { useRouter } from "next/navigation";
import { HOME_TYPE } from "@/components/PageHome/api/interface";
import OnboardingInfoCard from "@/components/ProfileSideDrawer/components/OnboardingInfoCard";
import PatientProfileInfo from "@/components/ProfileSideDrawer/components/PatientProfileInfo";
import ProfileBabyOnboardBanner from "@/components/ProfileSideDrawer/components/ProfileBabyOnboardBanner";
import ProfileLink from "@/components/ProfileSideDrawer/components/ProfileLink";
import UserProfile from "@/components/ProfileSideDrawer/components/UserProfile";
import drawerMenusInfo from "@/components/ProfileSideDrawer/Profile/profiledrawerMenus.json";
import { setIsOpenOnboardingSteps, setIsProfileOpen } from "@/components/ProfileSideDrawer/redux/slice";
import { dateToString, getPlainTexts } from "@/helpers";
import { DeviceInfo } from "@/interface/deviceInfo";
import firebaseRepo from "@/libs/firebase";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import authLogout from "@/redux/slice/auth/actions/authLogout";
import currentPatientSelector from "@/redux/slice/auth/selectors/currentPatient";
import { selectAllPatients } from "@/redux/slice/auth/selectors/patientDetails";
import UserOnboardedSelector from "@/redux/slice/auth/selectors/userOnboarded";
import SegmentInfoSelector from "@/redux/slice/auth/selectors/userSegmentInfo";
import { SignOutActions } from "@/redux/slice/auth/types/SignOutActions";
import routes from "@/helpers/router";
import { assetClickedEvent } from "@/helpers/TSDEvents";
import { NON_ONBOARDED_STATUS, ONBOARDING_STATUS } from "@/helpers/constants";
import styles from "./Profile.module.scss";

interface ProfileInterface {
  ua: DeviceInfo;
  isUserFromApp?: boolean;
}

const Profile = (props: ProfileInterface) => {
  const { isUserFromApp } = props;
  const dispatch = useAppDispatch();
  const apolloClient = useApolloClient();
  const currentPatientData = useAppSelector(currentPatientSelector);
  const patientAllProfiles = useAppSelector(selectAllPatients);
  const router = useRouter();
  const menusData = drawerMenusInfo?.data;
  const SegmentInfoObject = useAppSelector(SegmentInfoSelector);
  const isOnboarded = useAppSelector(UserOnboardedSelector);

  let PrimaryMenusArray = menusData?.profileSidebarLinks;
  const savedArticlesLink = {
    title: "Saved Articles and more",
    subtitle: "Article, videos and stories",
    url: routes.savedCollections(),
    icon: "icon-savedArticles",
  };

  if (isUserFromApp && isOnboarded) {
    PrimaryMenusArray = [savedArticlesLink];
  } else if (isUserFromApp) {
    PrimaryMenusArray = [];
  } else if (isOnboarded) {
    PrimaryMenusArray = [savedArticlesLink, ...PrimaryMenusArray];
  }

  const handleLoggedOut = () => {
    const signOutActions: SignOutActions = {
      dispatchSignOutUpdates() {
        authLogout({ apolloClient, dispatch });
      },
    };

    firebaseRepo.getInstance().signOut(signOutActions);

    return false;
  };

  let onboardingInfo: any = {};

  if (isOnboarded) {
    const onboardingData: any = SegmentInfoObject?.segmentDetail;

    if (SegmentInfoObject?.type === HOME_TYPE.PRECONCEPTION && onboardingData?.fertileWindow !== null) {
      const fertileWindowStart = onboardingData?.fertileWindow?.start
        ? dateToString(onboardingData?.fertileWindow?.start, "do MMM")
        : "";
      const fertileWindowEnd = onboardingData?.fertileWindow?.end
        ? dateToString(onboardingData?.fertileWindow?.end, "do MMM")
        : "";

      onboardingInfo = { ...onboardingData, fertileWindow: `${fertileWindowStart} - ${fertileWindowEnd}` };
    }

    if ([HOME_TYPE.PREGNANCY, HOME_TYPE.BIRTH].includes(SegmentInfoObject?.type) && onboardingData?.dueDate !== null) {
      const dueDateString = onboardingData?.dueDate ? dateToString(onboardingData?.dueDate, "do MMM''yy") : "";
      onboardingInfo = { ...onboardingData, dueDate: dueDateString };
    }

    if (SegmentInfoObject?.type === HOME_TYPE.NEW_MOM && onboardingData?.Birthday !== null) {
      onboardingInfo = { ...onboardingData, birthDate: onboardingData?.Birthday };
    }
  }

  const SidebarMenusList = (props: any) => {
    const { menuArray, isMore } = props;

    if (menuArray?.length === 0) {
      return null;
    } else {
      const handleMenuClick = (item: any) => {
        if (!item.hasOwnProperty("subMenu")) {
          dispatch(setIsProfileOpen(false));
          router.push(item.url);
        }
      };

      const handleSubMenuClick = (submenuData: any) => {
        const { menuItem, title: primaryMenuTitle, index } = submenuData;

        if (menuItem.url) {
          assetClickedEvent({
            asset_name: `Submenu of ${primaryMenuTitle}`,
            asset_display_text: menuItem?.label,
            asset_position: 1,
            asset_element_position: index + 1,
            momverse_stage: isOnboarded ? (ONBOARDING_STATUS[SegmentInfoObject?.type] ?? "") : NON_ONBOARDED_STATUS,
          });
          router.push(menuItem.url); // Redirect to the assigned URL
        } else if (menuItem.onClick) {
          menuItem.onClick(); // Execute the custom onClick function
        }
      };

      return menuArray?.map((item, index) => (
        <ProfileLink
          titleIconClassName={item?.icon}
          titleIconUrl={item?.titleIconUrl}
          IconSize={item?.IconSize}
          title={item?.title}
          subTitle={item?.subtitle}
          className={styles.link}
          subMenu={item?.subMenu}
          onClick={() => {
            assetClickedEvent({
              asset_name: "Profile Sidebar",
              asset_display_text: item?.title,
              asset_position: 1,
              asset_element_position: isMore ? PrimaryMenusArray?.length + index + 1 : index + 1,
              momverse_stage: isOnboarded ? (ONBOARDING_STATUS[SegmentInfoObject?.type] ?? "") : NON_ONBOARDED_STATUS,
            });
            handleMenuClick(item);
          }}
          handleSubMenuClick={handleSubMenuClick}
          key={index}
        />
      ));
    }
  };

  return (
    <div className={styles.profileRoot}>
      {/* Header */}
      <div className={styles.header}>
        <Typography component="h3" className={styles.title}>
          <span className={`icon-icon-left ${styles.leftArrow}`} onClick={() => dispatch(setIsProfileOpen(false))} />
          My Account
        </Typography>
        {!isUserFromApp && (
          <div className={styles.walletRoot}>
            <span className={`icon-Wallet ${styles.walletIcon}`} onClick={() => router.push("/oneapollo-membership")} />
          </div>
        )}
      </div>
      {!isUserFromApp && (
        <>
          <div className={styles.mainProfileBx}>
            <PatientProfileInfo
              name={`${currentPatientData?.firstName} ${currentPatientData?.lastName}`}
              mobileNumber={currentPatientData?.mobileNumber}
              uhid={currentPatientData?.uhid}
              imageUrl={currentPatientData?.photoUrl}
            />
          </div>

          <div className={styles.moreProfileRoot}>
            <div className={styles.head}>
              <Typography component="h3" className={styles.title}>
                More Profiles <span className={styles.profileNumber}>({patientAllProfiles?.length})</span>
              </Typography>
              <Button
                color="tertiarybaby"
                className={styles.btn}
                onClick={() => {
                  dispatch(setIsProfileOpen(false));
                  router.push("/my-account");
                }}
              >
                Manage / Add
              </Button>
            </div>
            <div className={styles.profileListingRoot}>
              <UserProfile
                imageUrl={currentPatientData?.photoUrl}
                title={`${currentPatientData?.firstName} ${currentPatientData?.lastName}`}
                imageSize={38}
                marginBottom={6}
                className={styles.profileCrad}
                imageBG="#F4C9C5"
                imageClassName={styles.imageBx}
              />
            </div>
          </div>
        </>
      )}
      {/* </div> */}

      <>
        {isOnboarded ? (
          <>
            <OnboardingInfoCard type={SegmentInfoObject?.type} onboardingInfo={onboardingInfo} />
          </>
        ) : (
          <div className={styles.onBoardBanner}>
            <ProfileBabyOnboardBanner
              btnClick={() => {
                dispatch(setIsOpenOnboardingSteps(true));
                dispatch(setIsProfileOpen(false));
              }}
            />
          </div>
        )}
      </>
      {/* list Link data */}
      <div className={`${styles.linkList}`}>
        <SidebarMenusList menuArray={PrimaryMenusArray} />
      </div>
      {!isUserFromApp && (
        <>
          <div className={`${styles.linkList} ${styles.moreLinkRoot}`}>
            <Typography component="h3" className={styles.linkTitle}>
              More
            </Typography>
            <SidebarMenusList menuArray={menusData?.profileSidebarMoreLinks} isMore={true} />
          </div>
          {/* Logout */}
          <div className={styles.logOut}>
            <Button className={styles.btn} onClick={handleLoggedOut} color="tertiarybaby">
              Logout <i className={`icon-logout ${styles.logOutIcon}`} />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default Profile;
